import React from 'react';
import './AccountPreservationScore.css';

interface AccountPreservationScoreProps {
  scenarios: any;
  lifeExpectancy: number;
  currentAge: number;
}

const AccountPreservationScore: React.FC<AccountPreservationScoreProps> = ({
  scenarios,
  lifeExpectancy,
  currentAge
}) => {
  if (!scenarios || !scenarios.average) {
    return <div className="preservation-score">No data available</div>;
  }

  const avgScenario = scenarios.average;
  const lowScenario = scenarios.low;
  const highScenario = scenarios.high;

  const avgFinalBalance = avgScenario.total_final_balance || 0;
  const lowFinalBalance = lowScenario?.total_final_balance || 0;

  // Get year-by-year data to check if running out early
  const detailedData = avgScenario.detailed_projection_data || [];
  const finalYear = detailedData[detailedData.length - 1];
  const finalAge = finalYear?.age || lifeExpectancy;

  // Check if money runs out before life expectancy
  const runsOutEarly = detailedData.some((year: any) => {
    const totalAssets = (year.rrsp_balance || 0) +
                       (year.tfsa_balance || 0) +
                       (year.non_registered_balance || 0) +
                       (year.rrif_balance || 0);
    return totalAssets <= 0 && year.age < lifeExpectancy;
  });

  // Calculate score based on outcomes
  const getScore = () => {
    // 🔴 Critical issues
    if (runsOutEarly) {
      return {
        score: 'critical',
        icon: '🔴',
        title: 'Strategy Needs Adjustment',
        message: 'Your plan shows risk of running out of money before life expectancy. Consider reducing spending or increasing retirement age.',
        recommendations: [
          'Reduce annual spending by 10-20%',
          'Delay retirement by 1-3 years',
          'Maximize government benefit delays (CPP to 70, OAS to 70)',
          'Review investment return assumptions',
          'Consider part-time work in early retirement'
        ]
      };
    }

    // Check low scenario
    if (lowFinalBalance < 0) {
      return {
        score: 'warning',
        icon: '🟡',
        title: 'Review Recommended',
        message: 'In worst-case scenarios (25th percentile), you may run out of money. Consider building more safety margin.',
        recommendations: [
          'Increase emergency fund in TFSA',
          'Reduce spending in early retirement years',
          'Keep 2-3 years of expenses in safe investments',
          'Consider annuity for guaranteed income floor'
        ]
      };
    }

    // Check if dying with too much money (inefficient)
    const yearlySpending = avgScenario.detailed_projection_data?.[0]?.annual_spending || 50000;
    const yearsOfSpending = avgFinalBalance / yearlySpending;

    if (avgFinalBalance > yearlySpending * 15) {
      return {
        score: 'review',
        icon: '🟡',
        title: 'Estate Planning Opportunity',
        message: `Your plan projects ending with ${Math.round(yearsOfSpending)} years of spending left (${formatCurrency(avgFinalBalance)}). Consider enjoying more now or planned gifting.`,
        recommendations: [
          'Increase discretionary spending in early retirement',
          'Consider tax-efficient gifting to children/grandchildren',
          'Maximize TFSA contributions for spouse',
          'Review charitable donation strategies',
          'Delay CPP/OAS if accumulating excess wealth'
        ]
      };
    }

    // 🟢 Optimal range
    return {
      score: 'optimal',
      icon: '🟢',
      title: 'Plan Looks Strong',
      message: `Your retirement strategy is well-balanced. Accounts last through life expectancy (age ${Math.round(finalAge)}) with ${formatCurrency(avgFinalBalance)} remaining.`,
      recommendations: [
        'Review annually and adjust for market changes',
        'Monitor actual spending vs. plan',
        'Rebalance portfolio as planned',
        'Consider updating life expectancy assumptions every 5 years'
      ]
    };
  };

  const score = getScore();

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(val);

  return (
    <div className="preservation-score">
      <h3>Account Preservation Score</h3>
      <p className="preservation-subtitle">Quality assessment of your retirement drawdown strategy</p>

      <div className={`score-card ${score.score}`}>
        <div className="score-icon">{score.icon}</div>
        <div className="score-content">
          <h4>{score.title}</h4>
          <p>{score.message}</p>
        </div>
      </div>

      <div className="scenario-summary">
        <div className="scenario-item">
          <div className="scenario-label">Worst Case (25th %ile)</div>
          <div className={`scenario-value ${lowFinalBalance < 0 ? 'negative' : 'positive'}`}>
            {formatCurrency(lowFinalBalance)}
          </div>
        </div>
        <div className="scenario-item">
          <div className="scenario-label">Average (50th %ile)</div>
          <div className={`scenario-value ${avgFinalBalance < 0 ? 'negative' : 'positive'}`}>
            {formatCurrency(avgFinalBalance)}
          </div>
        </div>
        <div className="scenario-item">
          <div className="scenario-label">Best Case (75th %ile)</div>
          <div className={`scenario-value positive`}>
            {formatCurrency(highScenario?.total_final_balance || 0)}
          </div>
        </div>
      </div>

      <div className="recommendations-section">
        <h4>Recommendations</h4>
        <ul>
          {score.recommendations.map((rec, idx) => (
            <li key={idx}>{rec}</li>
          ))}
        </ul>
      </div>

      <div className="score-methodology">
        <details>
          <summary>How is this score calculated?</summary>
          <div className="methodology-content">
            <p><strong>🟢 Optimal:</strong> Money lasts through life expectancy, ending balance is reasonable (not running out, not hoarding excessively)</p>
            <p><strong>🟡 Review:</strong> Either running out in worst-case scenarios OR ending with excessive wealth (&gt;15 years of spending)</p>
            <p><strong>🔴 Adjust:</strong> Running out of money before life expectancy in average scenario</p>
            <p className="note">This score helps identify if your drawdown strategy balances security and enjoyment.</p>
          </div>
        </details>
      </div>
    </div>
  );
};

export default AccountPreservationScore;
